import React from "react";
import { motion } from "motion/react";
import {
  Home,
  CreditCard,
  TrendingUp,
  PiggyBank,
  ShieldCheck,
  Receipt,
  Coins,
  BarChart3,
  ChevronRight,
} from "lucide-react";

export interface CategoryCardData {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  badge?: string;
  calculatorKey: string;
}

export const CATEGORY_ITEMS: CategoryCardData[] = [
  {
    id: "mortgage",
    name: "Mortgage",
    description: "Home loans & monthly payments",
    icon: Home,
    calculatorKey: "mortgage",
  },
  {
    id: "loan",
    name: "Loan",
    description: "Auto & personal loan terms",
    icon: CreditCard,
    calculatorKey: "loan",
  },
  {
    id: "investment",
    name: "Investment",
    description: "Compound growth & SIP projection",
    icon: TrendingUp,
    badge: "Popular",
    calculatorKey: "investment",
  },
  {
    id: "savings",
    name: "Savings",
    description: "Interest yield & CD growth",
    icon: PiggyBank,
    calculatorKey: "savings",
  },
  {
    id: "retirement",
    name: "Retirement",
    description: "401(k), IRA & FIRE goals",
    icon: ShieldCheck,
    calculatorKey: "retirement",
  },
  {
    id: "tax",
    name: "Tax",
    description: "Income brackets & net pay",
    icon: Receipt,
    calculatorKey: "tax",
  },
  {
    id: "debt",
    name: "Debt",
    description: "Payoff schedule & interest saved",
    icon: Coins,
    calculatorKey: "debt",
  },
  {
    id: "budget",
    name: "Budget",
    description: "50/30/20 expense allocation",
    icon: BarChart3,
    calculatorKey: "budget",
  },
];

interface CategoryCardsProps {
  selectedCategory: string;
  onSelectCategory: (categoryId: string) => void;
}

export const CategoryCards: React.FC<CategoryCardsProps> = ({
  selectedCategory,
  onSelectCategory,
}) => {
  return (
    <div className="w-full space-y-3">
      <div className="flex items-center justify-between text-xs font-mono font-medium text-slate-400">
        <span className="uppercase tracking-wider text-amber-400 font-semibold">
          Select Category to Try Calculator Live
        </span>
        <span className="hidden sm:inline text-slate-500">
          Instant Live Switch
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {CATEGORY_ITEMS.map((cat, index) => {
          const Icon = cat.icon;
          const isSelected = selectedCategory === cat.id;

          return (
            <motion.button
              key={cat.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.04 }}
              whileHover={{ y: -4, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelectCategory(cat.id)}
              className={`relative text-left p-3.5 rounded-2xl transition-all duration-300 cursor-pointer overflow-hidden group border ${
                isSelected
                  ? "bg-gradient-to-br from-amber-500/20 via-amber-500/10 to-transparent border-amber-500/60 shadow-lg shadow-amber-500/10"
                  : "bg-white/[0.04] hover:bg-white/[0.08] border-white/[0.08] hover:border-amber-500/30"
              }`}
            >
              {/* Subtle top-right glow accent when selected or hovered */}
              <div
                className={`absolute top-0 right-0 w-16 h-16 rounded-full blur-xl transition-opacity duration-300 pointer-events-none ${
                  isSelected
                    ? "bg-amber-400/30 opacity-100"
                    : "bg-amber-400/0 group-hover:bg-amber-400/20 opacity-0 group-hover:opacity-100"
                }`}
              />

              {/* Active bar on left */}
              {isSelected && (
                <motion.div
                  layoutId="activeCategoryBar"
                  className="absolute left-0 top-2 bottom-2 w-1 bg-amber-400 rounded-r-full"
                />
              )}

              <div className="flex items-start justify-between gap-2 mb-2">
                <div
                  className={`p-2.5 rounded-xl border transition-all duration-300 ${
                    isSelected
                      ? "bg-amber-500 text-slate-950 border-amber-400 shadow-md shadow-amber-500/20"
                      : "bg-white/5 border-white/10 text-amber-400 group-hover:bg-amber-500/20 group-hover:border-amber-500/30"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </div>

                {cat.badge && (
                  <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-amber-400/20 border border-amber-400/30 text-amber-300">
                    {cat.badge}
                  </span>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <h4
                    className={`font-sans font-bold text-sm tracking-tight transition-colors ${
                      isSelected
                        ? "text-amber-300"
                        : "text-white group-hover:text-amber-300"
                    }`}
                  >
                    {cat.name}
                  </h4>
                  <ChevronRight
                    className={`w-3.5 h-3.5 transition-transform duration-300 ${
                      isSelected
                        ? "text-amber-400 translate-x-0.5"
                        : "text-slate-600 group-hover:text-amber-400 group-hover:translate-x-0.5"
                    }`}
                  />
                </div>

                <p className="text-[11px] text-slate-400 font-light mt-0.5 line-clamp-1 leading-snug">
                  {cat.description}
                </p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};
